import { useToast } from '@lifebit-ai/lds'
import { useQuery } from '@tanstack/react-query'
import { HttpStatusCodes } from 'enums'

import { useOnQueryError } from 'hooks/useOnQueryError'
import { getDataAccessRequestVerification } from 'services/data-access-requests'
import {
  DataAccessRequestQueryOptions,
  withDataAccessRequestsEnabled
} from 'services/data-access-requests/hooks/withDataAccessRequestsEnabled'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type { DataAccessRequestVerificationDetail } from 'services/data-access-requests/types'
import { ApiErrorResponse } from 'services/types'

type DataAccessRequestVerificationQueryOptions = DataAccessRequestQueryOptions<
  DataAccessRequestVerificationDetail,
  ApiErrorResponse
> & {
  onError?: (error: ApiErrorResponse) => void
}

interface UseGetDataAccessRequestVerificationParams {
  requestId: string
  options?: DataAccessRequestVerificationQueryOptions
}

export const useGetDataAccessRequestVerification = ({
  requestId,
  options
}: UseGetDataAccessRequestVerificationParams) => {
  const toast = useToast()
  const { enabled: enabledOption, onError, ...restOptions } = options ?? {}

  const query = useQuery<DataAccessRequestVerificationDetail, ApiErrorResponse>({
    ...restOptions,
    queryKey: DataAccessRequestQueryKeys.Verification(requestId),
    queryFn: () => getDataAccessRequestVerification(requestId),
    enabled: withDataAccessRequestsEnabled(!!requestId && (enabledOption ?? true))
  })

  useOnQueryError(query, error => {
    if (onError) {
      onError(error)
      return
    }

    if (error.response?.status === HttpStatusCodes.NotFound) return

    toast({ message: 'Failed to get data access request details' })
  })

  return query
}
